import React, { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Stack, Typography, useTheme } from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { useCurrentPractice } from '../../contexts/PracticeContext'
import useStripeCapitalConnect from '../../hooks/useStripeCapitalConnect'
import { useCapitalAccess } from '../../hooks/useCapitalAccess'
import { trackCapitalInteractionOnce } from '../../services/capital'
import { useCapitalApplicationSubmitted } from '../../util/capitalSubmitted'
import { formatCapitalOfferAmount } from '../../util/capital'

/**
 * Sidebar Capital promo tile. Styled with dashboard theme tokens only —
 * no Capital-specific colors, weights, or radii beyond the shared
 * `palette.capital.tileBackground` surface tint the dashboard already uses
 * for Capital promo surfaces.
 *
 * Visibility gate:
 *   1. `isCapitalEnabled` flag true on the practice settings
 *   2. User is hospital manager, regional manager, or admin (`isManagerOrAdmin`)
 *   3. Backend reports the offer is in a promo state
 *   4. Offer amount is non-null
 */
const CapitalPromoTile: React.FC = () => {
	const { currentPracticeObject } = useCurrentPractice()
	const navigate = useNavigate()
	const theme = useTheme()
	const { canAccess } = useCapitalAccess()

	const consolidatedPracticeId = currentPracticeObject?.consolidatedPractice?.id
	const { offerId, offerAmount, offerStatus, isPromoOffer } = useStripeCapitalConnect(
		canAccess ? consolidatedPracticeId : undefined
	)

	// Hide the tile as soon as the user submits the application, even though
	// Stripe's server-side `offerStatus` transition can lag by minutes-to-hours.
	// Matches the optimistic flip the `/financing/capital` page does for its
	// main view so both surfaces stay consistent across the tab session.
	const applicationSubmitted = useCapitalApplicationSubmitted(consolidatedPracticeId)

	const willRender = canAccess && isPromoOffer && offerAmount !== null && !applicationSubmitted

	// Fires `promo_tile_viewed` only once the tile is actually scrolled into
	// view in the sidebar, not merely once it becomes eligible to render —
	// otherwise the event over-counts tiles that render off-screen.
	const tileRef = useRef<HTMLDivElement | null>(null)

	// Dedupe on the offer id, falling back to status+amount when the backend
	// omits it — analytics must never decide whether the tile renders.
	const viewDedupeKey = offerId ?? `${offerStatus}:${offerAmount}`

	useEffect(() => {
		if (!willRender || !consolidatedPracticeId) return undefined
		const node = tileRef.current
		if (!node || typeof IntersectionObserver === 'undefined') return undefined

		const observer = new IntersectionObserver((entries) => {
			if (!entries.some((entry) => entry.isIntersecting)) return
			trackCapitalInteractionOnce(consolidatedPracticeId, 'promo_tile_viewed', viewDedupeKey, {
				offerId,
				offerAmount,
				offerStatus
			})
			observer.disconnect()
		})
		observer.observe(node)

		return () => observer.disconnect()
	}, [willRender, consolidatedPracticeId, viewDedupeKey, offerId, offerAmount, offerStatus])

	if (!willRender) {
		return null
	}

	const goToCapital = (): void => navigate('/financing/capital', { state: { source: 'tile' } })

	return (
		<Box
			ref={tileRef}
			onClick={goToCapital}
			role="button"
			tabIndex={0}
			onKeyDown={(e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault()
					goToCapital()
				}
			}}
			sx={{
				width: '100%',
				backgroundColor: theme.palette.capital.tileBackground,
				borderRadius: 1,
				p: 1.5,
				cursor: 'pointer',
				transition: 'background-color 120ms ease',
				'&:hover': {
					backgroundColor: theme.palette.capital.tileBackgroundHover
				},
				'&:focus-visible': {
					outline: `2px solid ${theme.palette.primary.main}`,
					outlineOffset: 2
				}
			}}
		>
			{/* Stack with explicit uniform spacing — lets us ignore the theme's
			    `overline` lineHeight (2.46, meant for tab/label contexts) and
			    render all three lines with matching visual gaps. Each Typography
			    overrides lineHeight to a tight 1.3 so the Stack spacing is what
			    actually controls the vertical rhythm. */}
			<Stack spacing={1}>
				<Typography
					variant="overline"
					sx={{
						color: theme.palette.primary.main,
						display: 'block',
						lineHeight: 1.3
					}}
				>
					Scratch Capital
				</Typography>
				<Typography
					variant="body2"
					sx={{
						color: 'text.primary',
						fontWeight: 600,
						lineHeight: 1.3
					}}
				>
					Your financing offer is ready.
				</Typography>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between'
					}}
				>
					<Typography
						variant="body2"
						sx={{
							color: theme.palette.primary.main,
							fontWeight: 600,
							lineHeight: 1.3
						}}
					>
						Up to {formatCapitalOfferAmount(offerAmount)}
					</Typography>
					<ArrowForwardIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
				</Box>
			</Stack>
		</Box>
	)
}

export default CapitalPromoTile
